"use client";

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { SESSION_EXPIRED_EVENT, platformFetch, startSignIn } from "@/lib/platform-fetch";
import type { Schema } from "@/lib/api-types";

/** What `GET /v1/me` answers. Kept deliberately small -- see the route's own docstring. */
// Voice flags are role-resolved server-side (G80): `voice_basic` gates the composer mic and
// read-aloud, `voice_full` gates hands-free mode; neither implies the other, and the platform
// voice kill switch overrides both. Feature toggles have no per-role part -- the platform switch
// is the whole answer. Read sites keep `?? true` so a missing toggle never removes an affordance.
export type Me = Schema<"MeResponse">;

interface AuthState {
  /** `undefined` while the first `/v1/me` is in flight -- distinct from "signed out". */
  user: Me | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasAdminAccess: boolean;
  canAuthorSkills: boolean;
  canScheduleTasks: boolean;
  /** A role grants verbose mode: show its switch in the user menu. */
  verboseModeAvailable: boolean;
  skillsEnabled: boolean;
  voiceBasicEnabled: boolean;
  voiceFullModeEnabled: boolean;
  messageEditingEnabled: boolean;
  fileUploadsEnabled: boolean;
  interruptEnabled: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export const ME_QUERY_KEY = ["me"] as const;

async function fetchMe(): Promise<Me | null> {
  const res = await platformFetch("/v1/me");
  // **A 401 is an answer, not an error.** By the time it reaches here `platformFetch` has
  // already tried a refresh, so this means "not signed in" -- the normal state for a first-time
  // visitor. Throwing would put the whole app into an error boundary over a routine condition.
  if (res.status === 401) return null;
  if (!res.ok) {
    throw Object.assign(new Error(`/v1/me returned ${res.status}`), { status: res.status });
  }
  return (await res.json()) as Me;
}

/**
 * Identity for the whole app, from one `/v1/me` call.
 *
 * A provider rather than `useQuery(ME_QUERY_KEY)` at each call site. Auth is a single global
 * object, not a keyed cache of many things, and routing it through one context means route
 * guards, the header and the admin nav cannot disagree about who is signed in.
 *
 * The permission *set* is deliberately not here: `/v1/me` returns a boolean, because
 * per-permission decisions belong on the server (CLAUDE.md: "API responses are UI-ready ... No
 * business rules in clients"). `hasAdminAccess` decides whether the admin shell renders at all;
 * every action inside it is still enforced by `require_admin`, which 404s on a miss.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // **This was `staleTime: Infinity`, and that is no longer safe.** The reasoning then was
    // sound for what `/v1/me` carried: identity does not change while a tab is open, a sign-out
    // navigates away, and a role change takes effect at the next sign-in because that is when
    // roles are synced.
    //
    // It now also carries the platform feature toggles, which an admin flips at any moment and
    // which are expected to take effect on open tabs. With the cache pinned forever, an admin
    // who turned message editing off and clicked back to a chat -- a client-side route change,
    // no reload -- kept seeing the edit pencil, because this query never refetched. That was
    // reported as "I turned it off but it's still showing", and the setting was in fact written
    // correctly; only the browser disagreed.
    //
    // 30s plus a refetch on window focus, rather than back to `Infinity` with a manual
    // invalidation at the one call site that mutates it. An invalidation only fixes the tab
    // that performed the write; every *other* user's open tab is the case that actually
    // matters for a platform-wide kill switch. Structural sharing means an unchanged response
    // keeps its object identity, so the extra poll re-renders nothing.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    // `platformFetch` fires this once a refresh has already failed, so the session is genuinely
    // gone. Clearing the cache first matters: without it, the next render can paint a signed-in
    // shell from stale data before the navigation lands.
    function onExpired() {
      // `/setup` is the one page that must work signed out -- see PUBLIC_PATHS in worker/index.ts.
      // On a fresh environment `/v1/me` always fails there, and sending the browser to sign-in
      // lands on `sso-not-configured` before the wizard can run.
      if (/^\/setup(?:\/|$)/.test(window.location.pathname)) return;
      queryClient.clear();
      startSignIn();
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  const value: AuthState = {
    user: isPending ? undefined : (data ?? null),
    isLoading: isPending,
    isAuthenticated: !isPending && data != null,
    hasAdminAccess: data?.has_admin_access ?? false,
    // Defaulted false, like `hasAdminAccess`: an older server, or a `/v1/me` that has not
    // resolved yet, must hide the menu item rather than offer a page whose routes 404.
    canAuthorSkills: data?.can_author_skills ?? false,
    // `?? false`, like `canAuthorSkills`: the server already folds the role grant and the
    // platform switch together, and an older response that lacks the field must read as
    // "no" rather than revealing an entry the routes will refuse anyway.
    canScheduleTasks: data?.can_schedule_tasks ?? false,
    // Defaulted closed, like the role-granted voice pair: the server re-checks anyway.
    verboseModeAvailable: data?.verbose_mode_available ?? false,
    // `?? true`, unlike `canAuthorSkills` above. This gates an entry to a page that is useful
    // to anyone *granted* a skill, author or not, so a response that has not arrived yet must
    // not hide it -- the same "keep the affordance you already had" default the feature toggles
    // use. Not a boundary either way: ingress enforces the switch.
    skillsEnabled: data?.skills_enabled ?? true,
    voiceBasicEnabled: data?.voice_basic ?? false,
    voiceFullModeEnabled: data?.voice_full ?? false,
    // `?? true`, unlike the voice pair above. Those default closed because a voice entitlement
    // is something a role must grant; these default open because they are kill switches, and
    // the state of "we haven't heard yet" is the platform's existing behaviour, not "off".
    messageEditingEnabled: data?.message_editing_enabled ?? true,
    fileUploadsEnabled: data?.file_uploads_enabled ?? true,
    interruptEnabled: data?.interrupt_enabled ?? true,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) {
    // A hook used outside its provider is a wiring mistake that otherwise shows up as a blank
    // screen or a spurious redirect to sign-in. Naming it is cheaper than debugging it.
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return value;
}
